/**
 * Reusable Module Card Component
 * Displays feature module with toggle switch and description
 */

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Lock } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export function ModuleCard({
  title,
  description,
  icon: Icon,
  enabled,
  onToggle,
  disabled = false,
  disabledReason = '',
  pricingImpact = 0,
  testId,
  badge = null,
  children
}) {
  return (
    <Card 
      className="transition-shadow duration-200 hover:shadow-[0_16px_40px_rgba(15,23,42,0.10)]"
      data-testid={testId}
    >
      <CardHeader className="space-y-2">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            {Icon && (
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                <Icon className="w-5 h-5 text-primary" strokeWidth={1.75} />
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <CardTitle className="text-lg heading-font tracking-tight">
                  {title}
                </CardTitle>
                {badge && (
                  <Badge variant="outline" className="text-xs">
                    {badge}
                  </Badge>
                )}
              </div>
              <CardDescription className="text-sm">
                {description}
              </CardDescription>
              {pricingImpact > 0 && (
                <p className="text-xs text-muted-foreground mt-2 mono">
                  +${pricingImpact}/month
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2 flex-shrink-0">
            {disabled && disabledReason && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Lock className="w-4 h-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs max-w-xs">{disabledReason}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <Switch
              checked={enabled || false}
              onCheckedChange={onToggle}
              disabled={disabled}
              data-testid={`${testId}-toggle`}
            />
          </div>
        </div>
      </CardHeader>
      
      {children && (
        <CardContent className="pt-0">
          {children}
        </CardContent>
      )}
    </Card>
  );
}
